import React, { useState } from "react";
import KebabMenu from "../../../components/KebabMenu";
import Icon from "./Icon";
import SessionTimeline from "./SessionTimeline";
import { courseProgress, courseStatus, formatTime, sessionDateParts, sortedSessions } from "./classesModel";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const ROLE_LABELS = { LEAD_INSTRUCTOR: "Lead Instructor", ASSISTANT_INSTRUCTOR: "Assistant Instructor", TA: "TA" };
const formatRole = (role) => ROLE_LABELS[role]
  || role.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

function formatClosed(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function statusLabel(status, sessions) {
  if (status === "live") {
    const live = sessions.find((s) => s.enabled === true);
    return live?.sessionNumber != null ? `Live · Session ${live.sessionNumber}` : "Live";
  }
  return { complete: "Complete", draft: "Draft", upcoming: "Upcoming" }[status];
}

function nextLine(next, progress) {
  if (!next) return `${progress.attended} attended`;
  const parts = sessionDateParts(next);
  const start = formatTime(next.startTime);
  return `Next: ${parts ? `${parts.weekday}, ${parts.month} ${parts.day}` : "date to be set"}${start ? ` · ${start}` : ""}`;
}

/**
 * One assigned class: title and facts, progress and next session, primary actions, and an expandable session
 * timeline. Live classes start expanded so the Take attendance button is one click away.
 */
export default function ClassRow({ course, sessions, pendingRequests, now, actions }) {
  const status = courseStatus(course, sessions);
  const [open, setOpen] = useState(status === "live");
  const title = course.title || course.name;
  const ordered = sortedSessions(sessions);
  const progress = courseProgress(sessions, now);
  const enrolled = course.studentCount ?? 0;
  const location = course.location || ordered.find((s) => s.location)?.location;
  const label = statusLabel(status, sessions);
  const barPct = status === "complete" ? 100 : progress.total > 0 ? (progress.done / progress.total) * 100 : 0;
  const menu = [
    { icon: <Icon n="users" s={15} />, label: "View roster", onSelect: () => actions.onViewRoster(course) },
    ...(actions.onAddSession ? [{ icon: <Icon n="plus" s={15} />, label: "Add session", onSelect: () => actions.onAddSession(course) }] : []),
  ];

  return (
    <div className={`cls${open ? " open" : ""}`} data-testid={`class-${course.id}`}>
      <div className="row">
        <button type="button" className="tg" aria-expanded={open} aria-controls={`sess-${course.id}`}
          aria-label={`Show sessions for ${title}`} onClick={() => setOpen(!open)}>
          <Icon n="chev" s={16} />
        </button>

        <div className="main">
          <h3 className="title">{title}</h3>
          <div className="meta">
            <span className="cat">{course.category || "Course"}</span>
            {(course.courseNumber || course.code) && <span className="mono code">{course.courseNumber || course.code}</span>}
            {progress.total > 0 && <span><Icon n="cal" />{plural(progress.total, "session")}</span>}
            {course.studentCount != null && <span><Icon n="users" />{plural(course.studentCount, "student")} enrolled</span>}
            {location && <span><Icon n="pin" />{location}</span>}
            {course.teachingRoleAtClose && <span>Taught as <b>{formatRole(course.teachingRoleAtClose)}</b></span>}
            {course.completedAt && <span>Closed {formatClosed(course.completedAt)}</span>}
            {pendingRequests > 0 && <span className="pend">{pendingRequests} pending</span>}
          </div>
        </div>

        <div className="prog" aria-label={`${label}, ${progress.done} of ${progress.total} sessions held`}>
          <div className="top"><b className={status}>{label}</b><em>{progress.done} of {progress.total} sessions</em></div>
          <div className="bar"><i style={{ width: `${progress.total > 0 ? Math.max(barPct, 2) : 0}%` }} /></div>
          {progress.total > 0 && <div className="nx">{nextLine(progress.next, progress)}</div>}
        </div>

        <div className="acts">
          <button type="button" className="btn btn-o" onClick={() => actions.onOpenClass(course)}>Open class</button>
          <KebabMenu label={`More actions for ${title}`} triggerIcon={<Icon n="more" s={16} />} items={menu} />
        </div>
      </div>

      {open && (
        <div className="sess" id={`sess-${course.id}`}>
          {ordered.length > 0 ? (
            <div className="sess-in">
              <SessionTimeline title={title} sessions={ordered} next={progress.next} enrolled={enrolled} now={now}
                onTakeAttendance={(s) => actions.onTakeAttendance(course, s)}
                onViewAttendance={(s) => actions.onViewAttendance(course, s)}
                onSessionDetails={() => actions.onSessionDetails(course)} />
            </div>
          ) : <p className="muted">No sessions scheduled yet.</p>}
          <div className="sess-f">
            <span className="muted">
              {[course.documentCount > 0 && `${plural(course.documentCount, "document")} attached`,
                course.checklistCount > 0 && plural(course.checklistCount, "skill checklist")].filter(Boolean).join(" · ")}
            </span>
            <span>
              {actions.onAddSession && <button type="button" className="lnk" onClick={() => actions.onAddSession(course)}><Icon n="plus" />Add session</button>}
              <button type="button" className="lnk" onClick={() => actions.onViewRoster(course)}><Icon n="users" />View roster</button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
